import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, CheckCircle2, BookOpen, Brain, Heart, Compass, Sparkles, Loader2 } from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PILLARS = [
  {
    title: 'Tudo do Programa 90 Dias',
    desc: 'Inclui toda a base de admissão, aceitação, compreensão, sanidade, transformação e prática.',
    icon: CheckCircle2,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    title: 'Profundidade Emocional',
    desc: 'Exploração aprofundada de questões emocionais, padrões de comportamento e feridas que alimentam o ciclo da adicção.',
    icon: Heart,
    color: 'text-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
  },
  {
    title: 'Compreensão Sistêmica',
    desc: 'Visão ampliada das dinâmicas familiares, relacionais e sociais que influenciam a recuperação e a manutenção da sobriedade.',
    icon: Brain,
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
  },
  {
    title: 'Identidade e Propósito',
    desc: 'Reconstrução da identidade pessoal além da adicção. Exploração de valores, sentido de vida e futuro.',
    icon: Compass,
    color: 'text-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
  },
];

export default function Programa180Page() {
  const navigate = useNavigate();
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/api/programs/interest/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.registered_programs?.includes('180-dias')) {
        setRegistered(true);
      }
    } catch {}
  };

  const handleRegister = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Faça login para registrar interesse'); return; }
    setRegistering(true);
    try {
      const res = await axios.post(`${API}/api/programs/interest`, { program: '180-dias' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistered(true);
      toast.success(res.data.already_registered ? 'Interesse já registrado!' : 'Interesse registrado com sucesso!');
    } catch { toast.error('Erro ao registrar interesse'); }
    finally { setRegistering(false); }
  };

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto" data-testid="programa-180-page">
        {/* Back */}
        <Button onClick={() => navigate('/programas')} variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" data-testid="back-to-programas-180">
          <ArrowLeft className="h-4 w-4 mr-1" /> Programas
        </Button>

        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
            <Clock className="h-7 w-7 text-emerald-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold mb-1">Programa 180 Dias</h1>
          <p className="text-sm text-muted-foreground">Recuperação completa com profundidade ampliada</p>
          <Badge className="mt-2 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Método PAREI-AD — Premium+</Badge>
        </div>

        {/* Description */}
        <FadeInItem index={0}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-4">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-2 flex items-center gap-2"><BookOpen className="h-4 w-4 text-emerald-500" /> Sobre o Programa</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                O Programa de 180 Dias é a jornada completa de recuperação da U-Clean. Ele inclui toda a base do Programa de 90 Dias e avança para temas de profundidade emocional, compreensão sistêmica, construção de identidade e propósito de vida.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Com maior memória contextual da ARIA, relatórios mais profundos e módulos de reflexão avançados, este programa é voltado para quem busca consolidar a recuperação a longo prazo e reconstruir sua vida com clareza e sentido.
              </p>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* Pillars */}
        <h2 className="font-bold text-base mb-3 mt-6">Pilares do Programa</h2>
        <div className="space-y-3 mb-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeInItem key={i} index={i + 1}>
                <Card className="rounded-2xl border-border/40 shadow-sm" data-testid={`pillar-${i}`}>
                  <CardContent className="p-5 flex gap-3">
                    <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${p.color}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm mb-0.5">{p.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </FadeInItem>
            );
          })}
        </div>

        {/* Differentials */}
        <FadeInItem index={5}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-4">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-500" /> Diferenciais em relação ao 90 Dias</h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Maior memória contextual da ARIA para conversas mais personalizadas</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Relatórios mais profundos explorando aspectos emocionais e sistêmicos</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Módulos avançados sobre relações interpessoais e autoconhecimento</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Ressignificação de experiências e construção de futuro</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Perguntas mais complexas do Método PAREI-AD para explorar crenças e valores</li>
              </ul>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* What includes */}
        <FadeInItem index={6}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-6">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-3">O que inclui</h2>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> 180 atividades diárias</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Questionários completos</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Check-ins diários</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> ARIA com memória ampliada</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Tarefas personalizadas/dia</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Plano anti-recaída completo</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Relatórios aprofundados</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Economia AWA (gamificação)</div>
              </div>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* Content in development notice */}
        <FadeInItem index={7}>
          <Card className="rounded-2xl border-dashed border-amber-300 dark:border-amber-700/50 shadow-sm mb-6">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">
                O detalhamento dia a dia das fases exclusivas do Programa 180 Dias (dias 91–180) está em elaboração pela equipe U-Clean. O conteúdo será publicado progressivamente.
              </p>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* CTA */}
        <FadeInItem index={8}>
          <div className="text-center pb-8">
            {registered ? (
              <div className="space-y-2" data-testid="interest-registered-180">
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm px-4 py-1.5">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Interesse registrado
                </Badge>
                <p className="text-xs text-muted-foreground">Você será notificado quando o programa estiver disponível para início.</p>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleRegister}
                  disabled={registering}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-xl px-6"
                  data-testid="register-interest-180"
                >
                  {registering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Quero participar
                </Button>
                <p className="text-xs text-muted-foreground mt-2">Sem cobrança. Registre seu interesse para ser notificado.</p>
              </>
            )}
          </div>
        </FadeInItem>
      </div>
    </PageTransition>
  );
}
